import { useCallback } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { SendMessageVariables, useCancelTurn, useSendMessage } from '../../queries/statsAssistant'
import { StatsAssistantRequestError } from '../../../model/StatsAssistant'
import {
  patchTurn,
  statsAssistantProcessingUlidsAtom,
  statsAssistantTurnsAtom,
} from '../../../state/statsAssistant'

const getTurnErrorMessage = (error: Error): string =>
  error instanceof StatsAssistantRequestError && error.userMessage
    ? error.userMessage
    : 'Une erreur est survenue pendant l’analyse.'

export const useStatsAssistantTurn = () => {
  const setTurns = useSetAtom(statsAssistantTurnsAtom)
  const processingUlids = useAtomValue(statsAssistantProcessingUlidsAtom)
  const { mutate: sendMessage } = useSendMessage()
  const { mutate: cancelTurn } = useCancelTurn()

  const run = useCallback(
    (variables: SendMessageVariables) => {
      const { conversationUlid, message, replaceFromMessageUlid } = variables
      if (processingUlids.includes(conversationUlid)) return

      // « En attente » tant qu'aucun consumer n'a pris la question dans la file.
      setTurns((turns) =>
        patchTurn(turns, conversationUlid, {
          isProcessing: true,
          progressText: 'En attente…',
          error: null,
          failedRequest: null,
        }),
      )
      sendMessage(variables, {
        onError: (error) =>
          setTurns((turns) =>
            patchTurn(turns, conversationUlid, {
              isProcessing: false,
              progressText: null,
              error: getTurnErrorMessage(error),
              failedRequest: { message, replaceFromMessageUlid },
            }),
          ),
      })
    },
    [processingUlids, sendMessage, setTurns],
  )

  /** L'arrêt est confirmé par l'event done (status cancelled). */
  const cancel = useCallback(
    (conversationUlid: string) => {
      if (!processingUlids.includes(conversationUlid)) return
      setTurns((turns) => patchTurn(turns, conversationUlid, { progressText: 'Arrêt en cours…' }))
      cancelTurn(conversationUlid)
    },
    [cancelTurn, processingUlids, setTurns],
  )

  return { run, cancel }
}
